import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeBlock from './CodeBlock';
import './Message.css';
import { AiOutlineFile, AiOutlineEye, AiOutlineEyeInvisible, AiOutlineFileImage, AiOutlineCopy, AiOutlineEdit, AiOutlineCalendar, AiOutlineDown, AiOutlineRight, AiOutlineMail, AiOutlinePaperClip, AiOutlineDownload } from 'react-icons/ai';
import { BsFiletypePdf, BsFiletypeDocx, BsFiletypeTxt, BsFiletypeJson, BsFiletypeXlsx } from 'react-icons/bs';
import { downloadFileContent, saveFileToDevice } from '../../services/openaiService';

// Function to check if message has calendar information
const hasCalendarInfo = (content) => {
  // Define more specific calendar-related phrases to prevent false positives
  const calendarKeywords = [
    'calendar data', 
    'level 10 meeting', 
    'upcoming events',
    'scheduled meeting',
    'appointment details',
    'your schedule',
    'meeting details',
    'events on your calendar',
    'meeting invite',
    'meeting scheduled for'
  ];

  if (typeof content === 'string') {
    const lowerContent = content.toLowerCase();
    // Check for more specific calendar phrases
    return calendarKeywords.some(keyword => lowerContent.includes(keyword));
  } else if (Array.isArray(content) && content.length > 0 && content[0].type === 'text') {
    const textValue = content[0].text?.value || content[0].text || '';
    const lowerTextValue = textValue.toLowerCase();
    // Check for more specific calendar phrases
    return calendarKeywords.some(keyword => lowerTextValue.includes(keyword));
  }
  return false;
};

// Function to check if message has Gmail information
const hasGmailInfo = (content) => {
  const gmailKeywords = [
    'email data', 'gmail', 'inbox', 'received an email', 'sent email', 
    'searched email', 'attachment', 'forwarded', 'subject:', 'from:', 
    'compose email', 'draft email', 'email folder', 'email label', 
    'unread email', 'read email', 'email thread', 'reply to email',
    'message thread', 'email conversation', 'mail message', 'mail content',
    'email sender', 'email recipient', 'cc:', 'bcc:', 'email address',
    'starred email', 'archived email', 'email category', 'important email'
  ];

  if (typeof content === 'string') {
    const lowerContent = content.toLowerCase();
    return gmailKeywords.some(keyword => lowerContent.includes(keyword));
  } else if (Array.isArray(content) && content.length > 0 && content[0].type === 'text') {
    const textValue = content[0].text?.value || content[0].text || '';
    const lowerTextValue = textValue.toLowerCase();
    return gmailKeywords.some(keyword => lowerTextValue.includes(keyword));
  }
  return false;
};

// Component for extracted file content
const ExtractedFileContent = ({ content, isUser }) => {
  const [showContent, setShowContent] = useState(false);
  
  // Separate message text from extracted file content
  const contentParts = content.split(/\n*---\s*Extracted Content from Files\s*---\n*/);
  const messageText = contentParts[0];
  const extractedContent = contentParts.length > 1 ? contentParts[1] : '';
  
  // Extract file names from extracted content
  const fileMatches = extractedContent.match(/\[File \d+: ([^\]]+)\]/g) || [];
  const fileNames = fileMatches.map(match => match.replace(/\[File \d+: /, '').replace(/\]/, ''));
  
  return (
    <div>
      {/* Display the actual message text */}
      <div className={`prose prose-sm max-w-none ${isUser ? 'text-white prose-headings:text-white prose-p:text-white prose-strong:text-white' : ''}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{messageText}</ReactMarkdown>
      </div>
      
      {/* Display file attachments */}
      {fileNames.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center mb-2">
            <div className={`text-sm font-medium ${isUser ? 'text-indigo-100' : 'text-gray-700'}`}>
              Attached Files:
            </div>
            <button
              onClick={() => setShowContent(!showContent)}
              className={`ml-2 p-1 rounded-full ${isUser ? 'text-indigo-200 hover:text-white hover:bg-indigo-500' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-100'}`}
              title={showContent ? "Hide file content" : "Show file content"}
            >
              {showContent ? 
                <AiOutlineEyeInvisible className="h-4 w-4" /> : 
                <AiOutlineEye className="h-4 w-4" />
              }
            </button>
          </div>
          
          {fileNames.map((fileName, idx) => (
            <div key={idx} className={`flex items-center ${isUser ? 'bg-indigo-500 bg-opacity-30' : 'bg-gray-100'} rounded p-2 mb-2 text-sm`}>
              {fileName.toLowerCase().endsWith('.pdf') ? (
                <BsFiletypePdf className={`${isUser ? 'text-red-300' : 'text-red-500'} text-lg`} />
              ) : fileName.toLowerCase().endsWith('.docx') ? (
                <BsFiletypeDocx className={`${isUser ? 'text-blue-300' : 'text-blue-500'} text-lg`} />
              ) : fileName.toLowerCase().endsWith('.txt') ? (
                <BsFiletypeTxt className={`${isUser ? 'text-gray-300' : 'text-gray-500'} text-lg`} />
              ) : fileName.toLowerCase().endsWith('.json') ? (
                <BsFiletypeJson className={`${isUser ? 'text-yellow-300' : 'text-yellow-500'} text-lg`} />
              ) : fileName.toLowerCase().endsWith('.xlsx') || fileName.toLowerCase().endsWith('.xls') ? (
                <BsFiletypeXlsx className={`${isUser ? 'text-green-300' : 'text-green-500'} text-lg`} />
              ) : fileName.toLowerCase().match(/\.(jpe?g|png|gif|webp)$/i) ? (
                <AiOutlineFileImage className={`${isUser ? 'text-purple-300' : 'text-purple-500'} text-lg`} />
              ) : (
                <AiOutlineFile className={`${isUser ? 'text-gray-300' : 'text-gray-400'} text-lg`} />
              )}
              <div className={`ml-2 flex-1 truncate ${isUser ? 'text-indigo-100' : 'text-gray-700'}`}>
                {fileName}
              </div>
            </div>
          ))}
          
          {/* Show extracted content if button is clicked */}
          {showContent && extractedContent && (
            <div className={`mt-3 p-3 rounded-md text-sm ${isUser ? 'bg-indigo-500 bg-opacity-20 text-indigo-100' : 'bg-gray-100 text-gray-700'}`}>
              <div className={`font-medium mb-2 ${isUser ? 'text-white' : 'text-gray-900'}`}>Extracted Content:</div>
              <div className="whitespace-pre-wrap overflow-auto max-h-96 text-xs">
                {extractedContent}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Component to display information about tools used by the assistant
const ToolUsageInfo = ({ message, isUser }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // Check which tools were used based on explicit flags instead of content detection
  const isCalendarUsed = message.toolType === 'calendar' || message.toolType === 'calendar+gmail';
  const isGmailUsed = message.toolType === 'gmail' || message.toolType === 'calendar+gmail';
  
  // Return null if no tools were used
  if (!isCalendarUsed && !isGmailUsed) {
    return null;
  }
  
  // List of operations potentially performed with each tool
  const calendarOperations = [
    "Checked your calendar for availability",
    "Fetched upcoming events",
    "Created a calendar event",
    "Sent calendar invites",
    "Updated existing event details",
    "Checked for scheduling conflicts",
    "Found optimal meeting times", 
    "Analyzed your calendar patterns",
    "Located specific events or appointments"
  ];
  
  const gmailOperations = [
    "Checked inbox for relevant emails",
    "Composed and sent emails",
    "Searched for specific emails",
    "Organized email information by sender/topic",
    "Checked email attachments",
    "Processed email attachments",
    "Retrieved email content",
    "Searched for emails from specific senders",
    "Searched for emails with specific subject lines",
    "Drafted email responses",
    "Analyzed email patterns",
    "Forwarded email content",
    "Managed email folders/labels"
  ];

  return (
    <div className={`rounded-md p-3 mb-3 ${isUser ? 'bg-indigo-600 bg-opacity-30 border border-indigo-400' : 'bg-gray-50 border border-gray-200'}`}>
      <div className="flex justify-between items-center">
        <div className="font-medium text-sm flex flex-wrap items-center">
          {isCalendarUsed && (
            <div className="flex items-center mr-3 mb-1 sm:mb-0">
              <AiOutlineCalendar className="text-blue-500 mr-1" size={18} />
              <span className={isUser ? 'text-indigo-200' : 'text-blue-600'}>Calendar Tool Used</span>
            </div>
          )}
          {isGmailUsed && (
            <div className="flex items-center">
              <AiOutlineMail className="text-red-500 mr-1" size={18} />
              <span className={isUser ? 'text-indigo-200' : 'text-red-600'}>Gmail Tool Used</span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`ml-2 p-1 rounded text-xs ${isUser ? 'text-indigo-200 hover:text-white hover:bg-indigo-500' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'}`}
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </button>
      </div>
      
      {showDetails && (
        <div className="mt-2 text-sm">
          {isCalendarUsed && (
            <div className="mb-2">
              <div className={`font-medium mb-1 ${isUser ? 'text-indigo-100' : 'text-blue-600'}`}>Calendar Operations:</div>
              <ul className={`list-disc pl-5 ${isUser ? 'text-indigo-200' : 'text-gray-700'}`}>
                {calendarOperations.map((op, index) => (
                  <li key={`calendar-${index}`} className="text-xs mb-1">{op}</li>
                ))}
              </ul>
            </div>
          )}
          
          {isGmailUsed && (
            <div>
              <div className={`font-medium mb-1 ${isUser ? 'text-indigo-100' : 'text-red-600'}`}>Gmail Operations:</div>
              <ul className={`list-disc pl-5 ${isUser ? 'text-indigo-200' : 'text-gray-700'}`}>
                {gmailOperations.map((op, index) => (
                  <li key={`gmail-${index}`} className="text-xs mb-1">{op}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Component to display Gmail attachments
const GmailAttachments = ({ attachments, isUser }) => {
  const [showAttachments, setShowAttachments] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  if (!attachments || attachments.length === 0) return null;
  
  // Group attachments by type for better organization
  const groupedAttachments = {
    documents: attachments.filter(att => 
      att.filename.toLowerCase().endsWith('.pdf') || 
      att.filename.toLowerCase().endsWith('.docx') || 
      att.filename.toLowerCase().endsWith('.doc') || 
      att.filename.toLowerCase().endsWith('.txt')
    ),
    spreadsheets: attachments.filter(att => 
      att.filename.toLowerCase().endsWith('.xlsx') || 
      att.filename.toLowerCase().endsWith('.xls') || 
      att.filename.toLowerCase().endsWith('.csv')
    ),
    images: attachments.filter(att => 
      /\.(jpe?g|png|gif|webp|svg)$/i.test(att.filename)
    ),
    others: attachments.filter(att => 
      !att.filename.toLowerCase().endsWith('.pdf') && 
      !att.filename.toLowerCase().endsWith('.docx') && 
      !att.filename.toLowerCase().endsWith('.doc') && 
      !att.filename.toLowerCase().endsWith('.txt') && 
      !att.filename.toLowerCase().endsWith('.xlsx') && 
      !att.filename.toLowerCase().endsWith('.xls') && 
      !att.filename.toLowerCase().endsWith('.csv') &&
      !/\.(jpe?g|png|gif|webp|svg)$/i.test(att.filename)
    )
  };
  
  // Get counts for attachment summary
  const counts = {
    documents: groupedAttachments.documents.length,
    spreadsheets: groupedAttachments.spreadsheets.length,
    images: groupedAttachments.images.length,
    others: groupedAttachments.others.length
  };
  
  // Format attachment size
  const formatSize = (sizeInBytes) => {
    if (!sizeInBytes) return '';
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${Math.round(sizeInBytes / 1024)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Handle attachment click (for preview or other actions)
  const handleAttachmentClick = (attachment) => {
    setSelectedAttachment(selectedAttachment === attachment ? null : attachment);
  };
  
  // Get appropriate icon based on file type
  const getFileIcon = (filename) => {
    if (filename.toLowerCase().endsWith('.pdf')) {
      return <BsFiletypePdf className={`${isUser ? 'text-red-300' : 'text-red-500'} text-lg`} />;
    } else if (/\.(docx?|rtf)$/i.test(filename)) {
      return <BsFiletypeDocx className={`${isUser ? 'text-blue-300' : 'text-blue-500'} text-lg`} />;
    } else if (/\.(txt|md)$/i.test(filename)) {
      return <BsFiletypeTxt className={`${isUser ? 'text-gray-300' : 'text-gray-500'} text-lg`} />;
    } else if (/\.(json|xml)$/i.test(filename)) {
      return <BsFiletypeJson className={`${isUser ? 'text-yellow-300' : 'text-yellow-500'} text-lg`} />;
    } else if (/\.(xlsx?|csv)$/i.test(filename)) {
      return <BsFiletypeXlsx className={`${isUser ? 'text-green-300' : 'text-green-500'} text-lg`} />;
    } else if (/\.(jpe?g|png|gif|webp|svg)$/i.test(filename)) {
      return <AiOutlineFileImage className={`${isUser ? 'text-purple-300' : 'text-purple-500'} text-lg`} />;
    } else {
      return <AiOutlineFile className={`${isUser ? 'text-gray-300' : 'text-gray-400'} text-lg`} />;
    }
  };
  
  return (
    <div className={`mt-3 rounded-md ${isUser ? 'bg-indigo-500 bg-opacity-10' : 'bg-red-50'} p-3 border ${isUser ? 'border-indigo-300' : 'border-red-100'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-sm font-medium flex items-center ${isUser ? 'text-indigo-100' : 'text-gray-700'}`}>
          <AiOutlinePaperClip className={`mr-1 ${isUser ? 'text-indigo-100' : 'text-red-500'}`} />
          <span>Email Attachments ({attachments.length})</span>
          {!showAttachments && (
            <span className="ml-2 text-xs text-opacity-80">
              {counts.documents > 0 && `${counts.documents} document${counts.documents !== 1 ? 's' : ''}`}
              {counts.documents > 0 && (counts.spreadsheets > 0 || counts.images > 0 || counts.others > 0) && ', '}
              {counts.spreadsheets > 0 && `${counts.spreadsheets} spreadsheet${counts.spreadsheets !== 1 ? 's' : ''}`}
              {counts.spreadsheets > 0 && (counts.images > 0 || counts.others > 0) && ', '}
              {counts.images > 0 && `${counts.images} image${counts.images !== 1 ? 's' : ''}`}
              {counts.images > 0 && counts.others > 0 && ', '}
              {counts.others > 0 && `${counts.others} other file${counts.others !== 1 ? 's' : ''}`}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowAttachments(!showAttachments)}
          className={`ml-2 p-1 rounded-full ${isUser ? 'text-indigo-200 hover:text-white hover:bg-indigo-500' : 'text-gray-500 hover:text-red-600 hover:bg-gray-100'}`}
          title={showAttachments ? "Hide attachments" : "Show attachments"}
        >
          {showAttachments ? 
            <AiOutlineEyeInvisible className="h-4 w-4" /> : 
            <AiOutlineEye className="h-4 w-4" />
          }
        </button>
      </div>
      
      {showAttachments && (
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
          {attachments.map((attachment, idx) => (
            <div 
              key={idx} 
              className={`flex items-center p-2 rounded text-sm ${isUser ? 'bg-indigo-500 bg-opacity-20 hover:bg-opacity-30' : 'bg-white hover:bg-gray-50'} cursor-pointer transition-colors`}
              onClick={() => handleAttachmentClick(attachment)}
            >
              {getFileIcon(attachment.filename)}
              <div className="ml-2 flex-1 min-w-0">
                <div className={`truncate ${isUser ? 'text-indigo-100' : 'text-gray-700'} font-medium`}>
                  {attachment.filename}
                </div>
                <div className={`text-xs ${isUser ? 'text-indigo-200' : 'text-gray-500'}`}>
                  {formatSize(attachment.size)}
                  {attachment.contentType && ` • ${attachment.contentType.split('/')[1]?.toUpperCase() || attachment.contentType}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {selectedAttachment && (
        <div className={`mt-3 p-2 rounded-md ${isUser ? 'bg-indigo-600 bg-opacity-30 border border-indigo-400' : 'bg-white border border-gray-200'}`}>
          <div className="flex justify-between items-center mb-2">
            <div className={`font-medium ${isUser ? 'text-white' : 'text-gray-800'}`}>
              {selectedAttachment.filename}
            </div>
            <button 
              onClick={() => setSelectedAttachment(null)}
              className={`text-xs ${isUser ? 'text-indigo-200 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
            >
              Close
            </button>
          </div>
          <div className={`text-xs ${isUser ? 'text-indigo-200' : 'text-gray-600'}`}>
            <p>File Type: {selectedAttachment.contentType || 'Unknown'}</p>
            <p>Size: {formatSize(selectedAttachment.size)}</p>
            {selectedAttachment.messageId && <p>From Email: {selectedAttachment.messageId}</p>}
            <p className="mt-2 italic">
              Select "Analyze Attachment" in chat to process this file's content
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Component to display and handle files generated by Code Interpreter
const CodeInterpreterFiles = ({ message, isUser }) => {
  const [isDownloading, setIsDownloading] = useState({});
  const [downloadError, setDownloadError] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState({});

  // Extract file IDs from message
  const fileIds = extractFileIds(message);
  
  // If there are no file IDs, return null
  if (fileIds.length === 0) {
    return null;
  }

  // Function to handle file download
  const handleDownload = async (fileId, index) => {
    try {
      setIsDownloading({...isDownloading, [fileId]: true});
      setDownloadError(null);
      
      console.log(`Downloading file with ID: ${fileId}`);
      
      // Get file content and metadata
      const fileData = await downloadFileContent(fileId);
      
      // Save file to device
      const success = saveFileToDevice(
        fileData.arrayBuffer, 
        fileData.filename,
        fileData.contentType
      );
      
      if (!success) {
        throw new Error('Failed to save file to device');
      }
      
      setIsDownloading({...isDownloading, [fileId]: false});
      setDownloadSuccess({...downloadSuccess, [fileId]: true});
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setDownloadSuccess(prev => ({...prev, [fileId]: false}));
      }, 3000);
      
    } catch (error) {
      console.error('Error downloading file:', error);
      setIsDownloading({...isDownloading, [fileId]: false});
      setDownloadError(`Failed to download file: ${error.message}`);
    }
  };

  // Determine the file type icon
  const getFileIcon = (fileId) => {
    // We can't determine the file type without metadata, 
    // but we can guess based on the sandbox path if available
    if (typeof fileId === 'object' && fileId.sandbox_path) {
      const path = fileId.sandbox_path.toLowerCase();
      if (path.endsWith('.png') || path.endsWith('.jpg') || path.endsWith('.jpeg') || path.endsWith('.gif')) {
        return <AiOutlineFileImage className="h-5 w-5 text-purple-500" />;
      } else if (path.endsWith('.pdf')) {
        return <BsFiletypePdf className="h-5 w-5 text-red-500" />;
      } else if (path.endsWith('.csv') || path.endsWith('.xlsx') || path.endsWith('.xls')) {
        return <BsFiletypeXlsx className="h-5 w-5 text-green-500" />;
      } else if (path.endsWith('.txt')) {
        return <BsFiletypeTxt className="h-5 w-5 text-gray-500" />;
      } else if (path.endsWith('.json')) {
        return <BsFiletypeJson className="h-5 w-5 text-yellow-500" />;
      }
    }
    
    // Default file icon
    return <AiOutlineFile className="h-5 w-5 text-blue-500" />;
  };

  // Get file name from the file ID object or generate a default name
  const getFileName = (fileId, index) => {
    if (typeof fileId === 'object' && fileId.sandbox_path) {
      return fileId.sandbox_path.split('/').pop();
    }
    
    // Try to extract extension from message content
    let extension = '';
    if (message.content && Array.isArray(message.content)) {
      for (const item of message.content) {
        if (item.type === 'text' && item.text && item.text.value) {
          // Look for common file extensions in the message
          const extensions = ['.csv', '.png', '.jpg', '.jpeg', '.pdf', '.txt', '.json', '.xlsx', '.xls'];
          for (const ext of extensions) {
            if (item.text.value.includes(ext)) {
              extension = ext;
              break;
            }
          }
          if (extension) break;
        }
      }
    }
    
    return `Generated_File_${index + 1}${extension}`;
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="font-medium text-gray-700 mb-2 flex items-center">
        <span className="mr-2">Generated Files</span>
        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
          {fileIds.length} {fileIds.length === 1 ? 'file' : 'files'}
        </span>
      </div>
      
      {fileIds.map((fileId, index) => {
        const id = typeof fileId === 'object' ? fileId.id : fileId;
        const fileName = getFileName(fileId, index);
        
        return (
          <div 
            key={`${id}-${index}`} 
            className="bg-blue-50 rounded-md p-3 border border-blue-100 flex flex-col"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                {getFileIcon(fileId)}
                <span className="ml-2 font-medium text-gray-800">
                  {fileName}
                </span>
              </div>
              <button
                onClick={() => handleDownload(id, index)}
                disabled={isDownloading[id]}
                className={`flex items-center px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isDownloading[id]
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : downloadSuccess[id]
                    ? 'bg-green-500 text-white'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {isDownloading[id] ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-1.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Downloading...
                  </>
                ) : downloadSuccess[id] ? (
                  <>
                    <svg className="h-4 w-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Downloaded
                  </>
                ) : (
                  <>
                    <AiOutlineDownload className="mr-1.5 h-4 w-4" />
                    Download File
                  </>
                )}
              </button>
            </div>
            
            {downloadError && (
              <div className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded">
                {downloadError}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Helper function to check if a message has file annotations
const hasFileAnnotations = (content) => {
  if (!content || !Array.isArray(content)) return false;
  
  return content.some(item => {
    // Check for text annotations
    if (item.type === 'text' && item.text && item.text.annotations) {
      return item.text.annotations.some(ann => 
        ann.type === 'file_path' && ann.file_path && ann.file_path.file_id
      );
    }
    
    // Check for image_file type
    return item.type === 'image_file' && item.image_file && item.image_file.file_id;
  });
};

// Helper function to extract file IDs from a message
const extractFileIds = (message) => {
  const fileIds = [];
  
  // Check for direct fileId on the message
  if (message.fileId) {
    fileIds.push(message.fileId);
  }
  
  // Check for fileIds array
  if (message.fileIds && Array.isArray(message.fileIds)) {
    message.fileIds.forEach(id => {
      if (!fileIds.includes(id)) {
        fileIds.push(id);
      }
    });
  }
  
  // Check for direct file_ids array (from API)
  if (message.file_ids && Array.isArray(message.file_ids)) {
    message.file_ids.forEach(id => {
      if (!fileIds.includes(id)) {
        fileIds.push(id);
      }
    });
  }
  
  // Check for attachments
  if (message.attachments && Array.isArray(message.attachments)) {
    message.attachments.forEach(attachment => {
      if (attachment.file_id && !fileIds.includes(attachment.file_id)) {
        fileIds.push(attachment.file_id);
      }
    });
  }
  
  // Check for content array
  if (message.content && Array.isArray(message.content)) {
    message.content.forEach(item => {
      // Check for image_file type
      if (item.type === 'image_file' && item.image_file && item.image_file.file_id) {
        const fileId = item.image_file.file_id;
        if (!fileIds.includes(fileId)) {
          fileIds.push(fileId);
        }
      }
      
      // Check for file_attachment type
      if (item.type === 'file_attachment' && item.file_attachment && item.file_attachment.file_id) {
        const fileId = item.file_attachment.file_id;
        if (!fileIds.includes(fileId)) {
          fileIds.push(fileId);
        }
      }
      
      // Check for text annotations
      if (item.type === 'text' && item.text) {
        // Handle annotations array
        if (item.text.annotations && Array.isArray(item.text.annotations)) {
          item.text.annotations.forEach(ann => {
            if (ann.type === 'file_path' && ann.file_path && ann.file_path.file_id) {
              const fileId = ann.file_path.file_id;
              if (!fileIds.includes(fileId)) {
                fileIds.push({
                  id: fileId,
                  sandbox_path: ann.text
                });
              }
            }
          });
        }
        
        // Also check for file references in the text content itself
        // This is a backup for when annotations might not be properly detected
        if (item.text.value) {
          const filePathMatches = item.text.value.match(/sandbox:\/[^\s)]+/g);
          if (filePathMatches) {
            console.log('Found potential file paths in text:', filePathMatches);
          }
        }
      }
    });
  }
  
  // Log extracted file IDs for debugging
  if (fileIds.length > 0) {
    console.log('Extracted file IDs from message:', fileIds);
  }
  
  return fileIds;
};

// New component to display calendar UI
const CalendarDisplay = ({ message, isUser }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // This is a placeholder for where you'd extract calendar data from the message
  // In a real implementation, you'd format and display actual calendar data
  const calendarData = extractCalendarData(message);
  
  return (
    <div className={`rounded-md p-3 mt-3 ${isUser ? 'bg-indigo-600 bg-opacity-10' : 'bg-blue-50'} border ${isUser ? 'border-indigo-300' : 'border-blue-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`font-medium flex items-center ${isUser ? 'text-indigo-100' : 'text-blue-700'}`}>
          <AiOutlineCalendar className={`mr-2 ${isUser ? 'text-indigo-200' : 'text-blue-500'}`} />
          <span>Calendar Information</span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`ml-2 p-1 rounded-full ${isUser ? 'text-indigo-200 hover:bg-indigo-500 hover:text-white' : 'text-blue-500 hover:bg-blue-100'}`}
        >
          {isExpanded ? <AiOutlineDown className="h-4 w-4" /> : <AiOutlineRight className="h-4 w-4" />}
        </button>
      </div>
      
      {isExpanded && (
        <div className={`text-sm ${isUser ? 'text-indigo-100' : 'text-gray-700'}`}>
          {calendarData.events.length > 0 ? (
            <div className="space-y-2">
              {calendarData.events.map((event, idx) => (
                <div key={idx} className={`p-2 rounded ${isUser ? 'bg-indigo-500 bg-opacity-20' : 'bg-white border border-blue-100'}`}>
                  <div className={`font-medium ${isUser ? 'text-white' : 'text-blue-700'}`}>{event.title}</div>
                  <div className="flex justify-between text-xs mt-1">
                    <span>{event.time}</span>
                    <span>{event.date}</span>
                  </div>
                  {event.description && (
                    <div className={`mt-1 text-xs ${isUser ? 'text-indigo-200' : 'text-gray-600'}`}>
                      {event.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-2 italic">
              Calendar data was accessed but no specific events to display
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to extract calendar data from message
const extractCalendarData = (message) => {
  // This is a placeholder - in a real implementation, you'd extract real data
  // from the message content or from a separate API call
  
  // Default to empty events array
  const data = {
    events: []
  };
  
  // Check if message content contains calendar event information
  // This is simplified - you would need more robust parsing in a real app
  if (message.content && Array.isArray(message.content)) {
    message.content.forEach(item => {
      if (item.type === 'text' && item.text && item.text.value) {
        const textValue = item.text.value;
        
        // Very basic parsing for demo purposes
        // In a real app, you'd have structured data from your backend
        const eventRegex = /meeting|appointment|event/i;
        const dateRegex = /(\w+ \d+(?:st|nd|rd|th)?|tomorrow|today|yesterday)/i;
        const timeRegex = /(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i;
        
        if (eventRegex.test(textValue)) {
          // Extract potential event information
          const dateMatch = textValue.match(dateRegex);
          const timeMatch = textValue.match(timeRegex);
          
          if (dateMatch || timeMatch) {
            // Find a potential title
            let title = "Calendar Event";
            const titleRegex = /"([^"]+)"|'([^']+)'/;
            const titleMatch = textValue.match(titleRegex);
            if (titleMatch) {
              title = titleMatch[1] || titleMatch[2];
            }
            
            data.events.push({
              title: title,
              date: dateMatch ? dateMatch[0] : "No date specified",
              time: timeMatch ? timeMatch[0] : "No time specified",
              description: "Details extracted from AI response"
            });
          }
        }
      }
    });
  }
  
  return data;
};

function Message({ message, isUser, onEditMessage, messageIndex }) {
  // Add state for copy button feedback first, before any conditionals
  const [isCopied, setIsCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const textareaRef = useRef(null);
  
  // Check if message content exists
  if (!message || !message.content) {
    return null;
  }
  
  // Determine if this is a tool usage message
  const isUsingTools = 
    message.role === 'assistant' && 
    message.content.length === 1 && 
    message.content[0].type === 'text' && 
    message.content[0].text.value && 
    (message.content[0].text.value.startsWith('Thinking') ||
     message.content[0].text.value.startsWith('Connecting with') ||
     message.usingTool === true);

  // Determine which tools are being used
  const isCalendarToolUsed = isUsingTools && (
    message.toolType === 'calendar' || 
    message.toolType === 'calendar+gmail'
  );
  
  const isGmailToolUsed = isUsingTools && (
    message.toolType === 'gmail' || 
    message.toolType === 'calendar+gmail'
  );
  
  const isCodeInterpreterUsed = isUsingTools && (
    message.toolType === 'code_interpreter'
  );
  
  // Check if message is currently streaming - only rely on the explicit isStreaming flag
  const isStreaming = 
    message.role === 'assistant' && 
    message.isStreaming === true; // Only use the explicit flag, don't infer from content
  
  // Check if message has a file ID (from code interpreter)
  const hasFileOutput = message.fileId || hasFileAnnotations(message.content);
  
  // Check if message contains extracted file content
  const hasExtractedContent = (content) => {
    if (typeof content === 'string') {
      return content.includes('--- Extracted Content from Files ---');
    } else if (Array.isArray(content) && content.length > 0 && content[0].type === 'text') {
      return content[0].text && 
        (content[0].text.value ? 
          content[0].text.value.includes('--- Extracted Content from Files ---') : 
          content[0].text.includes('--- Extracted Content from Files ---'));
    }
    return false;
  };
  
  // Function to render different content types
  const renderContent = (content) => {
    if (isUsingTools) {
      // Show appropriate tool usage message
      return (
        <div className="tool-usage-container p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
          <div className="flex items-center space-x-3">
            <div className="text-sm font-medium text-blue-600">
              {isCalendarToolUsed && isGmailToolUsed ? (
                <div className="flex items-center">
                  <AiOutlineCalendar className="mr-2 text-blue-500" />
                  <AiOutlineMail className="mr-2 text-red-500" />
                  Connecting with Calendar & Gmail
                </div>
              ) : isCalendarToolUsed ? (
                <div className="flex items-center">
                  <AiOutlineCalendar className="mr-2 text-blue-500" />
                  Connecting with Calendar
                </div>
              ) : isGmailToolUsed ? (
                <div className="flex items-center">
                  <AiOutlineMail className="mr-2 text-red-500" />
                  Connecting with Gmail
                </div>
              ) : (
                "EOS AI is thinking"
              )}
            </div>
            
            <div className="flex space-x-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      );
    }
    
    if (typeof content === 'string') {
      // Check if message contains extracted file content
      if (hasExtractedContent(content)) {
        return <ExtractedFileContent content={content} isUser={isUser} />;
      }
      
      // Handle legacy string content
      return (
        <div className={`prose prose-sm max-w-none ${isUser ? 'text-white prose-headings:text-white prose-p:text-white prose-strong:text-white' : ''}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      );
    }
    
    // Handle structured content array
    if (Array.isArray(content)) {
      // Check for extracted file content in the first text element
      if (content.length > 0 && content[0].type === 'text') {
        const textValue = content[0].text?.value || content[0].text;
        if (textValue && hasExtractedContent(textValue)) {
          return <ExtractedFileContent content={textValue} isUser={isUser} />;
        }
      }
      
      return content.map((item, index) => {
        if (!item) return null; // Skip null/undefined items
        
        switch (item.type) {
          case 'text':
            return (
              <div key={index} className={`prose prose-sm max-w-none ${isUser ? 'text-white prose-headings:text-white prose-p:text-white prose-strong:text-white' : ''}`}>
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom rendering for code blocks
                    code: ({ node, inline, className, children, ...props }) => {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <CodeBlock
                          language={match[1]}
                          value={String(children).replace(/\n$/, '')}
                          {...props}
                        />
                      ) : (
                        <code className={`${inline ? (isUser ? 'bg-orange-700 text-orange-100' : 'bg-gray-100') : ''} px-1 py-0.5 rounded text-sm font-mono`} {...props}>
                          {children}
                        </code>
                      );
                    },
                    // For links, open in new tab with smooth animation
                    a: ({ node, children, ...props }) => (
                      <a 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`${isUser ? 'text-orange-100 hover:text-white underline decoration-2 decoration-orange-300 hover:decoration-white' : 'text-blue-600 hover:text-blue-800 underline decoration-2 decoration-blue-300 hover:decoration-blue-600'}`} 
                        {...props}
                      >
                        {children}
                      </a>
                    ),
                    // Enhanced headings
                    h1: ({ node, children, ...props }) => (
                      <h1 className={`text-2xl font-heading font-bold mb-4 border-b ${isUser ? 'border-indigo-500 text-white' : 'border-gray-200 text-gray-900'} pb-2`} {...props}>
                        {children}
                      </h1>
                    ),
                    h2: ({ node, children, ...props }) => (
                      <h2 className={`text-xl font-heading font-semibold mb-3 mt-6 ${isUser ? 'text-white' : 'text-gray-800'}`} {...props}>
                        {children}
                      </h2>
                    ),
                    h3: ({ node, children, ...props }) => (
                      <h3 className={`text-lg font-heading font-medium mb-2 mt-5 ${isUser ? 'text-white' : 'text-gray-700'}`} {...props}>
                        {children}
                      </h3>
                    ),
                    // Enhanced lists
                    ul: ({ node, children, ...props }) => (
                      <ul className={`list-disc pl-5 space-y-1 my-3 ${isUser ? 'text-white' : ''}`} {...props}>
                        {children}
                      </ul>
                    ),
                    ol: ({ node, children, ...props }) => (
                      <ol className={`list-decimal pl-5 space-y-1 my-3 ${isUser ? 'text-white' : ''}`} {...props}>
                        {children}
                      </ol>
                    ),
                    // Enhanced paragraphs with better spacing
                    p: ({ node, children, ...props }) => (
                      <p className={`my-2 leading-relaxed ${isUser ? 'text-white' : ''}`} {...props}>
                        {children}
                      </p>
                    ),
                    // Enhanced blockquote
                    blockquote: ({ node, children, ...props }) => (
                      <blockquote className={`border-l-4 ${isUser ? 'border-indigo-300 text-indigo-100' : 'border-gray-300 text-gray-700'} pl-4 italic my-4`} {...props}>
                        {children}
                      </blockquote>
                    )
                  }}
                >
                  {item.text && item.text.value ? item.text.value : item.text || ''}
                </ReactMarkdown>
              </div>
            );
          // Add support for other content types as needed
          default:
            return (
              <div key={index} className={`${isUser ? 'text-indigo-100' : 'text-gray-500'} italic`}>
                Unsupported content type: {item.type}
              </div>
            );
        }
      });
    }
    
    // Fallback for any other content format
    return <div className={`${isUser ? 'text-indigo-100' : 'text-gray-500'} italic`}>Unsupported message format</div>;
  };
  
  // Add function to copy message content
  const copyMessageToClipboard = () => {
    let textToCopy = '';
    
    if (typeof message.content === 'string') {
      textToCopy = message.content;
    } else if (Array.isArray(message.content)) {
      // Extract text from content array
      textToCopy = message.content
        .filter(item => item && item.type === 'text')
        .map(item => item.text && item.text.value ? item.text.value : item.text || '')
        .join('\n');
    }
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        // Show temporary feedback
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch(err => console.error('Failed to copy message: ', err));
  };
  
  // Add function to handle edit mode
  const handleEditClick = () => {
    // Only allow editing user messages
    if (!isUser) return;
    
    // Extract current message content
    let currentContent = '';
    if (typeof message.content === 'string') {
      currentContent = message.content;
    } else if (Array.isArray(message.content)) {
      currentContent = message.content
        .filter(item => item && item.type === 'text')
        .map(item => item.text && item.text.value ? item.text.value : item.text || '')
        .join('\n');
    }
    
    setEditedContent(currentContent);
    setIsEditing(true);
    
    // Focus the textarea after state update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 10);
  };
  
  // Handle saving the edited message
  const handleSaveEdit = () => {
    if (editedContent.trim() && onEditMessage) {
      onEditMessage(messageIndex, editedContent);
      setIsEditing(false);
    }
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
  };
  
  // Auto-adjust textarea height
  const adjustTextareaHeight = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Check if this is a calendar or gmail-enabled message - use toolType property
  const isCalendarMessage = !isUser && !isUsingTools && message.toolType === 'calendar';
  const isGmailMessage = !isUser && !isUsingTools && message.toolType === 'gmail';
  const isCombinedToolMessage = !isUser && !isUsingTools && message.toolType === 'calendar+gmail';
  
  // Check if the message has email attachments
  const hasEmailAttachments = message.emailAttachments && message.emailAttachments.length > 0;

  // Check if this message used the calendar tool
  const calendarToolWasUsed = message.calendarToolUsed === true;
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div 
        className={`
          max-w-3xl rounded-eos px-5 py-4
          ${isUser 
            ? 'message-user bg-gradient-to-br from-orange-500 to-orange-600 text-white border border-orange-400' 
            : 'message-assistant bg-white text-gray-800 border border-gray-100'}
        `}
        style={{
          borderRadius: '1rem',
          boxShadow: isUser 
            ? '0 0 35px 8px rgba(234, 88, 12, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.2), inset 0 -1px 1px rgba(0, 0, 0, 0.05)' 
            : '0 0 35px 8px rgba(0, 0, 0, 0.06), inset 0 1px 1px rgba(255, 255, 255, 0.7), inset 0 -1px 1px rgba(0, 0, 0, 0.05)'
        }}
      >
        {isEditing ? (
          <div className="edit-container">
            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              onInput={adjustTextareaHeight}
              className="w-full bg-white text-gray-800 rounded p-3 border border-orange-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none resize-none min-h-[100px]"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 text-xs text-white bg-orange-500 hover:bg-orange-600 rounded"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            {renderContent(message.content)}
            
            {/* Show code interpreter file output if present */}
            {!isUser && hasFileOutput && !isUsingTools && <CodeInterpreterFiles message={message} isUser={isUser} />}
            
            {/* Show tool usage info if this message used calendar or gmail */}
            {(isCalendarMessage || isGmailMessage || isCombinedToolMessage) && 
              <ToolUsageInfo 
                message={message} 
                isUser={isUser} 
              />}
            
            {/* Show calendar UI if the tool was used */}
            {!isUser && calendarToolWasUsed && 
              <CalendarDisplay 
                message={message} 
                isUser={isUser} 
              />}
            
            {/* Show email attachments if present */}
            {hasEmailAttachments && 
              <GmailAttachments 
                attachments={message.emailAttachments} 
                isUser={isUser} 
              />}
            
            {!isUsingTools && (
              <div 
                className={`
                  text-xs mt-3 flex items-center justify-between
                  ${isUser ? 'text-orange-200' : 'text-gray-400'}
                `}
              >
                <div className="flex items-center">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-3 w-3 mr-1" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  <span>
                    {message.timestamp instanceof Date ? 
                      message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                      message.timestamp && typeof message.timestamp === 'object' && message.timestamp.toDate ? 
                      message.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                      new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    
                    {message.edited && (
                      <span className="ml-2 italic opacity-80">(edited)</span>
                    )}
                  </span>
                </div>
                
                <div className="flex items-center">
                  {/* Edit button - only for user messages */}
                  {isUser && (
                    <button
                      onClick={handleEditClick}
                      className={`mr-2 p-1 rounded-full hover:bg-opacity-10 relative ${
                        isUser ? 'hover:bg-orange-700 text-orange-200 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'
                      }`}
                      title="Edit message"
                    >
                      <AiOutlineEdit className="h-3.5 w-3.5" />
                    </button>
                  )}
                  
                  {/* Copy button */}
                  <button
                    onClick={copyMessageToClipboard}
                    className={`p-1 rounded-full hover:bg-opacity-10 relative ${
                      isUser ? 'hover:bg-orange-700 text-orange-200 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'
                    }`}
                    title={isCopied ? "Copied!" : "Copy message"}
                  >
                    <AiOutlineCopy className="h-3.5 w-3.5" />
                    {isCopied && (
                      <span className={`absolute -top-7 -left-3 text-xs px-2 py-1 rounded ${
                        isUser ? 'bg-orange-700 text-white' : 'bg-gray-700 text-white'
                      } whitespace-nowrap`}>
                        Copied!
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Message;